import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { UserPlus, Check, X, Mail, Phone, Award, Clock, Link2, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import api, { mensajeDeError, pesos } from '../lib/api';
import { toast } from 'sonner';

/**
 * Revisión de postulaciones de psicólogos.
 *
 * Registrarse no da acceso: cada postulación se revisa aquí. Antes de aprobar
 * hay que verificar el número de registro ante la Superintendencia de Salud.
 */
export const AdminPostulaciones = ({ onCambio }) => {
  const [postulaciones, setPostulaciones] = useState([]);
  const [filtro, setFiltro] = useState('pending');
  const [cargando, setCargando] = useState(true);
  const [enRevision, setEnRevision] = useState(null);
  const [accion, setAccion] = useState(null);
  const [nota, setNota] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [enlaceCopiado, setEnlaceCopiado] = useState(false);

  const enlaceRegistro = `${window.location.origin}/unete`;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/admin/postulaciones', {
        params: filtro === 'all' ? {} : { status: filtro },
      });
      setPostulaciones(data);
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos cargar las postulaciones'));
    } finally {
      setCargando(false);
    }
  }, [filtro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const resolver = async () => {
    if (!enRevision || !accion) return;

    setProcesando(true);
    try {
      await api.post(`/admin/postulaciones/${enRevision.id}/${accion}`, { note: nota });
      toast.success(accion === 'aprobar' ? 'Profesional aprobado y avisado por correo' : 'Postulación rechazada');
      setEnRevision(null);
      setAccion(null);
      setNota('');
      cargar();
      if (onCambio) onCambio();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos procesar la postulación'));
    } finally {
      setProcesando(false);
    }
  };

  const copiarEnlace = async () => {
    try {
      await navigator.clipboard.writeText(enlaceRegistro);
      setEnlaceCopiado(true);
      setTimeout(() => setEnlaceCopiado(false), 2500);
    } catch {
      toast.error('No se pudo copiar. Cópialo a mano.');
    }
  };

  const ESTADOS = {
    pending: { texto: 'En revisión', clase: 'bg-amber-500/20 text-amber-300' },
    approved: { texto: 'Aprobada', clase: 'bg-green-500/20 text-green-300' },
    rejected: { texto: 'Rechazada', clase: 'bg-red-500/20 text-red-300' },
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <UserPlus className="w-5 h-5" />
          Postulaciones de profesionales
        </CardTitle>
        <CardDescription className="text-slate-400">
          Verifica el número de registro antes de aprobar. Al aprobar, la persona recibe
          un correo y puede entrar con la clave que eligió.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Enlace privado para invitar */}
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg bg-slate-900/60 mb-6">
          <Link2 className="w-4 h-4 text-slate-400 flex-none" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-300">Enlace privado de registro</p>
            <p className="font-mono text-xs text-slate-400 truncate">{enlaceRegistro}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={copiarEnlace}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            {enlaceCopiado ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {enlaceCopiado ? 'Copiado' : 'Copiar'}
          </Button>
        </div>

        <div className="flex gap-2 mb-5 flex-wrap">
          {[
            ['pending', 'En revisión'],
            ['approved', 'Aprobadas'],
            ['rejected', 'Rechazadas'],
            ['all', 'Todas'],
          ].map(([valor, etiqueta]) => (
            <Button
              key={valor}
              size="sm"
              variant={filtro === valor ? 'default' : 'outline'}
              onClick={() => setFiltro(valor)}
              className={
                filtro === valor
                  ? 'bg-teal-600 hover:bg-teal-700 text-white'
                  : 'border-slate-600 text-slate-300 hover:bg-slate-700'
              }
            >
              {etiqueta}
            </Button>
          ))}
        </div>

        {cargando && <p className="text-slate-400 py-6">Cargando…</p>}

        {!cargando && postulaciones.length === 0 && (
          <p className="text-slate-400 py-8 text-center">
            {filtro === 'pending'
              ? 'No hay postulaciones esperando revisión.'
              : 'No hay postulaciones en este estado.'}
          </p>
        )}

        <div className="space-y-3">
          {postulaciones.map((p) => {
            const estado = ESTADOS[p.status] || ESTADOS.pending;

            return (
              <div key={p.id} className="p-4 rounded-lg bg-slate-900/60">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium text-lg">{p.name}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400 mt-1">
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" />
                        {p.email}
                      </span>
                      {p.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5" />
                          {p.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5" />
                        Registro {p.registration_number}
                      </span>
                      {p.years_experience > 0 && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {p.years_experience} años
                        </span>
                      )}
                    </div>
                  </div>

                  <Badge className={`${estado.clase} border-0`}>{estado.texto}</Badge>
                </div>

                {p.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {p.specialties.map((e) => (
                      <Badge key={e} className="bg-slate-700 text-slate-200 border-0 text-xs">
                        {e}
                      </Badge>
                    ))}
                  </div>
                )}

                {p.bio && <p className="text-slate-300 text-sm mb-3">{p.bio}</p>}

                {p.education.length > 0 && (
                  <p className="text-slate-400 text-sm mb-3">
                    <span className="text-slate-400">Formación: </span>
                    {p.education.join(' · ')}
                  </p>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-slate-400">
                    Valor por sesión {pesos(p.price_from)} · postuló el{' '}
                    {format(parseISO(p.created_at), "d 'de' MMMM yyyy", { locale: es })}
                  </p>

                  {p.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setEnRevision(p);
                          setAccion('aprobar');
                          setNota('');
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Aprobar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEnRevision(p);
                          setAccion('rechazar');
                          setNota('');
                        }}
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Rechazar
                      </Button>
                    </div>
                  )}
                </div>

                {p.review_note && (
                  <p className="text-sm text-slate-400 mt-3 pt-3 border-t border-slate-700">
                    <span className="text-slate-400">Nota: </span>
                    {p.review_note}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={Boolean(enRevision)} onOpenChange={() => setEnRevision(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>
              {accion === 'aprobar' ? '¿Aprobar a este profesional?' : '¿Rechazar la postulación?'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {accion === 'aprobar' ? (
                <>
                  <strong className="text-slate-200">{enRevision?.name}</strong> podrá entrar a su
                  panel y aparecerá en el listado público. Confirma antes que su registro{' '}
                  <strong className="text-slate-200">{enRevision?.registration_number}</strong> sea
                  válido en la Superintendencia de Salud.
                </>
              ) : (
                <>Se le enviará un correo avisándole. Puedes explicarle el motivo abajo.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div>
            <Label htmlFor="nota" className="text-slate-300">
              {accion === 'aprobar' ? 'Nota interna (opcional)' : 'Motivo (se le envía)'}
            </Label>
            <Textarea
              id="nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={3}
              className="mt-1 bg-slate-900 border-slate-700 text-white"
              placeholder={
                accion === 'aprobar'
                  ? 'Registro verificado el…'
                  : 'Falta acreditar el título profesional.'
              }
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEnRevision(null)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Volver
            </Button>
            <Button
              onClick={resolver}
              disabled={procesando}
              className={
                accion === 'aprobar'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {procesando ? 'Procesando…' : accion === 'aprobar' ? 'Sí, aprobar' : 'Sí, rechazar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminPostulaciones;
